import React, { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Button, Form, Modal } from 'react-bootstrap';
import { formatDateTime } from '../utils/DateUtils';
import { getKnockoutWinner, getKnockoutAnnotationLines, isWalkover, getWalkoverLabel } from '../utils/CupResult';
import { useAuth } from '../context/AuthContext';
import EditNoteIcon from '@mui/icons-material/EditNote';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import SportsScoreIcon from '@mui/icons-material/SportsSoccer';
import { SvgIcon } from '@mui/material';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const YellowCardIcon = () => (
    <SvgIcon style={{ fontSize: '1.1em' }}>
        <rect x="6" y="2" width="12" height="20" rx="2" fill="#fdd835" />
    </SvgIcon>
);

const SecondYellowCardIcon = () => (
    <SvgIcon style={{ fontSize: '1.1em' }}>
        <defs>
            <linearGradient id="matchCardGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="50%" stopColor="#fdd835" />
                <stop offset="50%" stopColor="#e53935" />
            </linearGradient>
        </defs>
        <rect x="6" y="2" width="12" height="20" rx="2" fill="url(#matchCardGradient)" />
    </SvgIcon>
);

const RedCardIcon = () => (
    <SvgIcon style={{ fontSize: '1.1em' }}>
        <rect x="6" y="2" width="12" height="20" rx="2" fill="#e53935" />
    </SvgIcon>
);

const CardIcon = ({ kind }) => {
    if (kind === 'yellow') return <YellowCardIcon />;
    if (kind === 'second_yellow') return <SecondYellowCardIcon />;
    if (kind === 'red') return <RedCardIcon />;
    return null;
};

const CARD_OPTIONS = [
    { value: 'yellow', label: 'Żółta' },
    { value: 'red', label: 'Czerwona (bezpośrednia)' },
    { value: 'second_yellow', label: 'Czerwona (druga żółta)' }
];

const MatchDetails = () => {
    const { matchId } = useParams();
    const { isAuthenticated } = useAuth();
    const [matchDetails, setMatchDetails] = useState(null);

    // Player lists (fetched lazily on first modal open)
    const [homePlayers, setHomePlayers] = useState([]);
    const [awayPlayers, setAwayPlayers] = useState([]);
    const [playersFetched, setPlayersFetched] = useState(false);

    // Score modal
    const [showScoreModal, setShowScoreModal] = useState(false);
    const [scoreForm, setScoreForm] = useState({ home_goals: null, away_goals: null, home_penalties: '', away_penalties: '', after_extra_time: false, walkover: '', walkover_enabled: false });
    const [scoreError, setScoreError] = useState(null);

    // Date modal
    const [showDateModal, setShowDateModal] = useState(false);
    const [dateForm, setDateForm] = useState('');
    const [dateError, setDateError] = useState(null);

    // Goal modal
    const [showGoalModal, setShowGoalModal] = useState(false);
    const [goalForm, setGoalForm] = useState({ player_id: null, is_own_goal: false, team_id: null });
    const [editingGoalId, setEditingGoalId] = useState(null);
    const [goalError, setGoalError] = useState(null);

    // Goal delete confirmation
    const [showDeleteGoalModal, setShowDeleteGoalModal] = useState(false);
    const [deleteGoalTarget, setDeleteGoalTarget] = useState(null);
    const [deleteGoalError, setDeleteGoalError] = useState(null);

    // Card modal
    const [showCardModal, setShowCardModal] = useState(false);
    const [cardForm, setCardForm] = useState({ player_id: null, kind: null, team_id: null });
    const [editingCardId, setEditingCardId] = useState(null);
    const [cardError, setCardError] = useState(null);

    // Card delete confirmation
    const [showDeleteCardModal, setShowDeleteCardModal] = useState(false);
    const [deleteCardTarget, setDeleteCardTarget] = useState(null);
    const [deleteCardError, setDeleteCardError] = useState(null);

    const fetchMatchDetails = useCallback(async () => {
        try {
            const response = await axios.get(`${BACKEND_URL}/details/match/${matchId}`);
            setMatchDetails(response.data);
        } catch (error) {
            console.error(error);
        }
    }, [matchId]);

    useEffect(() => {
        if (matchId) fetchMatchDetails();
    }, [matchId, fetchMatchDetails]);

    const ensurePlayersFetched = useCallback(async () => {
        if (playersFetched || !matchDetails) return;
        try {
            const [homeRes, awayRes] = await Promise.all([
                axios.get(`${BACKEND_URL}/details/team/${matchDetails.home_team_id}`),
                axios.get(`${BACKEND_URL}/details/team/${matchDetails.away_team_id}`)
            ]);
            setHomePlayers(homeRes.data.players || []);
            setAwayPlayers(awayRes.data.players || []);
            setPlayersFetched(true);
        } catch (err) {
            console.error('Błąd podczas pobierania zawodników:', err);
        }
    }, [playersFetched, matchDetails]);

    // --- SCORE ---
    const openScoreModal = () => {
        setScoreForm({
            home_goals: matchDetails.home_result ?? '',
            away_goals: matchDetails.away_result ?? '',
            home_penalties: matchDetails.home_penalties ?? '',
            away_penalties: matchDetails.away_penalties ?? '',
            after_extra_time: matchDetails.after_extra_time ?? false,
            walkover: matchDetails.walkover ?? '',
            walkover_enabled: !!matchDetails.walkover
        });
        setScoreError(null);
        setShowScoreModal(true);
    };

    const handleSaveScore = async () => {
        setScoreError(null);
        if (scoreForm.walkover_enabled && scoreForm.walkover === '') {
            setScoreError('Wybierz rodzaj walkoweru.');
            return;
        }
        const walkover = scoreForm.walkover_enabled ? scoreForm.walkover : null;
        try {
            await axios.post(
                `${BACKEND_URL}/details/update/match/score/${matchId}`,
                {
                    home_goals: scoreForm.home_goals === '' ? null : scoreForm.home_goals,
                    away_goals: scoreForm.away_goals === '' ? null : scoreForm.away_goals,
                    home_penalties: scoreForm.home_penalties === '' ? null : scoreForm.home_penalties,
                    away_penalties: scoreForm.away_penalties === '' ? null : scoreForm.away_penalties,
                    after_extra_time: scoreForm.after_extra_time,
                    walkover
                },
                { withCredentials: true }
            );
            setShowScoreModal(false);
            await fetchMatchDetails();
        } catch (err) {
            setScoreError(err.response?.data?.message || 'Błąd zapisu wyniku.');
        }
    };

    // Przełącznik "Walkower" — odsłania/ukrywa wybór rodzaju walkoweru
    const handleWalkoverToggle = (checked) => {
        setScoreForm(f => ({ ...f, walkover_enabled: checked, walkover: '' }));
    };

    // Zmiana rodzaju walkoweru — auto-wypełnia domyślny wynik (edytowalny dla jednostronnego)
    const handleWalkoverChange = (value) => {
        setScoreForm(f => {
            if (value === 'home') return { ...f, walkover: value, home_goals: '3', away_goals: '0', home_penalties: '', away_penalties: '', after_extra_time: false };
            if (value === 'away') return { ...f, walkover: value, home_goals: '0', away_goals: '3', home_penalties: '', away_penalties: '', after_extra_time: false };
            if (value === 'both') return { ...f, walkover: value, home_goals: '0', away_goals: '0', home_penalties: '', away_penalties: '', after_extra_time: false };
            return { ...f, walkover: '' };
        });
    };

    // --- DATE ---
    const openDateModal = () => {
        setDateForm(matchDetails.date || '');
        setDateError(null);
        setShowDateModal(true);
    };

    const handleSaveDate = async () => {
        setDateError(null);
        try {
            await axios.post(
                `${BACKEND_URL}/details/update/match/datetime/${matchId}`,
                { date: dateForm || null },
                { withCredentials: true }
            );
            setShowDateModal(false);
            await fetchMatchDetails();
        } catch (err) {
            setDateError(err.response?.data?.message || 'Błąd zapisu daty.');
        }
    };

    // --- GOALS ---
    const openAddGoalModal = async (teamId) => {
        await ensurePlayersFetched();
        setGoalForm({ player_id: '', is_own_goal: false, team_id: teamId });
        setEditingGoalId(null);
        setGoalError(null);
        setShowGoalModal(true);
    };

    const openEditGoalModal = async (goal, teamId) => {
        await ensurePlayersFetched();
        setGoalForm({
            player_id: goal.player_id || '',
            is_own_goal: goal.is_own_goal || false,
            team_id: teamId
        });
        setEditingGoalId(goal.id);
        setGoalError(null);
        setShowGoalModal(true);
    };

    const handleGoalSubmit = async () => {
        setGoalError(null);
        if (!goalForm.is_own_goal && !goalForm.player_id) {
            setGoalError('Wybierz zawodnika lub zaznacz bramkę samobójczą.');
            return;
        }
        try {
            const payload = {
                player_id: goalForm.is_own_goal ? null : (goalForm.player_id || null),
                is_own_goal: goalForm.is_own_goal
            };
            if (editingGoalId) {
                await axios.put(
                    `${BACKEND_URL}/goals/update/${editingGoalId}`,
                    payload,
                    { withCredentials: true }
                );
            } else {
                await axios.post(
                    `${BACKEND_URL}/goals/add`,
                    { match_id: parseInt(matchId), team_id: goalForm.team_id, ...payload },
                    { withCredentials: true }
                );
            }
            setShowGoalModal(false);
            await fetchMatchDetails();
        } catch (err) {
            setGoalError(err.response?.data?.message || 'Błąd podczas zapisywania bramki.');
        }
    };

    const openDeleteGoalModal = (goal) => {
        setDeleteGoalTarget(goal);
        setDeleteGoalError(null);
        setShowDeleteGoalModal(true);
    };

    const handleDeleteGoal = async () => {
        setDeleteGoalError(null);
        try {
            await axios.delete(
                `${BACKEND_URL}/goals/delete/${deleteGoalTarget.id}`,
                { withCredentials: true }
            );
            setShowDeleteGoalModal(false);
            await fetchMatchDetails();
        } catch (err) {
            setDeleteGoalError(err.response?.data?.message || 'Błąd podczas usuwania bramki.');
        }
    };

    // --- CARDS ---
    const openAddCardModal = async (teamId) => {
        await ensurePlayersFetched();
        setCardForm({ player_id: null, kind: 'yellow', team_id: teamId });
        setEditingCardId(null);
        setCardError(null);
        setShowCardModal(true);
    };

    const openEditCardModal = async (card, teamId) => {
        await ensurePlayersFetched();
        setCardForm({
            player_id: card.player_id || null,
            kind: card.kind || null,
            team_id: teamId
        });
        setEditingCardId(card.id);
        setCardError(null);
        setShowCardModal(true);
    };

    const handleCardSubmit = async () => {
        setCardError(null);
        if (!cardForm.player_id) {
            setCardError('Wybierz zawodnika.');
            return;
        }
        if (!cardForm.kind) {
            setCardError('Wybierz rodzaj kartki.');
            return;
        }
        try {
            const payload = {
                player_id: cardForm.player_id ? parseInt(cardForm.player_id) : null,
                kind: cardForm.kind
            };
            if (editingCardId) {
                await axios.put(
                    `${BACKEND_URL}/cards/update/${editingCardId}`,
                    payload,
                    { withCredentials: true }
                );
            } else {
                await axios.post(
                    `${BACKEND_URL}/cards/add`,
                    { match_id: parseInt(matchId), ...payload },
                    { withCredentials: true }
                );
            }
            setShowCardModal(false);
            await fetchMatchDetails();
        } catch (err) {
            setCardError(err.response?.data?.message || 'Błąd podczas zapisywania kartki.');
        }
    };

    const openDeleteCardModal = (card) => {
        setDeleteCardTarget(card);
        setDeleteCardError(null);
        setShowDeleteCardModal(true);
    };

    const handleDeleteCard = async () => {
        setDeleteCardError(null);
        try {
            await axios.delete(
                `${BACKEND_URL}/cards/delete/${deleteCardTarget.id}`,
                { withCredentials: true }
            );
            setShowDeleteCardModal(false);
            await fetchMatchDetails();
        } catch (err) {
            setDeleteCardError(err.response?.data?.message || 'Błąd podczas usuwania kartki.');
        }
    };

    // Helper: get player list for a given team_id
    const getPlayersForTeam = (teamId) => {
        if (!matchDetails) return [];
        if (teamId === matchDetails.home_team_id) return homePlayers;
        if (teamId === matchDetails.away_team_id) return awayPlayers;
        return [];
    };

    if (!matchDetails) {
        return <div>Ładowanie...</div>;
    }

    // Knockout: wyznacz zwycięzcę i adnotację (po dogrywce / karne) do wyświetlenia
    const isKnockout = matchDetails.type === 'cup_knockout';
    const knockoutMatch = {
        home_goals: matchDetails.home_result,
        away_goals: matchDetails.away_result,
        home_penalties: matchDetails.home_penalties,
        away_penalties: matchDetails.away_penalties,
        after_extra_time: matchDetails.after_extra_time
    };
    const matchIsWalkover = isWalkover(matchDetails);
    const walkoverLabel = getWalkoverLabel(matchDetails);
    const isDoubleWalkover = matchDetails.walkover === 'both';
    const winner = isKnockout ? getKnockoutWinner(knockoutMatch) : null;
    const knockoutAnnotationLines = isKnockout ? getKnockoutAnnotationLines(knockoutMatch) : [];
    // Zwycięzca pogrubiony, przegrany wyszarzony (tylko gdy mecz rozstrzygnięty)
    const teamNameStyle = (side) => ({
        fontSize: '1.1em',
        textAlign: 'center',
        ...(winner && winner !== side ? { color: '#999', fontWeight: 400 } : {})
    });

    // --- AGGREGATE GOALS BY PLAYER (visual only, for non-authenticated view) ---
    const aggregateGoals = (goals) => {
        if (!goals || goals.length === 0) return [];
        const groups = [];
        const map = new Map();
        for (const goal of goals) {
            if (goal.is_own_goal) {
                groups.push({ first_name: goal.first_name, last_name: goal.last_name, is_own_goal: true, count: 1 });
            } else {
                const key = `${goal.first_name}_${goal.last_name}`;
                if (map.has(key)) {
                    map.get(key).count++;
                } else {
                    const entry = { first_name: goal.first_name, last_name: goal.last_name, is_own_goal: false, count: 1 };
                    groups.push(entry);
                    map.set(key, entry);
                }
            }
        }
        return groups;
    };

    // --- RENDER GOAL LIST ---
    const renderGoalList = (teamName, goals, teamId, side) => (
        <div style={{ textAlign: side === 'home' ? 'right' : 'left' }}>
            <div style={{ marginTop: '6px' }}>
                {(goals ?? []).length === 0 && (
                    <div style={{ fontSize: '0.85em', color: '#999' }}>Brak bramek</div>
                )}
                {isAuthenticated
                    ? (goals ?? []).map((goal, index) => (
                        <div key={`goal-${teamId}-${index}`} style={{ marginBottom: '4px', fontSize: '0.85em', display: 'flex', alignItems: 'center', justifyContent: side === 'home' ? 'flex-end' : 'flex-start', gap: '4px', whiteSpace: 'nowrap' }}>
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                {goal.is_own_goal
                                    ? <>
                                        <span style={{ color: '#d32f2f' }}>Bramka samobójcza</span>
                                        <SportsScoreIcon style={{ fontSize: '1.1em', color: '#d32f2f' }} />
                                      </>
                                    : <>
                                        {goal.first_name} {goal.last_name}
                                        <SportsScoreIcon style={{ fontSize: '1.1em' }} />
                                      </>}
                            </span>
                            <EditNoteIcon
                                style={{ cursor: 'pointer', fontSize: '1.1em' }}
                                onClick={() => openEditGoalModal(goal, teamId)}
                            />
                            <DeleteForeverIcon
                                style={{ cursor: 'pointer', color: 'red', fontSize: '1.1em' }}
                                onClick={() => openDeleteGoalModal(goal)}
                            />
                        </div>
                    ))
                    : aggregateGoals(goals).map((group, index) => (
                        <div key={`goal-agg-${teamId}-${index}`} style={{ marginBottom: '4px', fontSize: '0.85em', display: 'flex', alignItems: 'center', justifyContent: side === 'home' ? 'flex-end' : 'flex-start', gap: '2px', whiteSpace: 'nowrap' }}>
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '2px' }}>
                                {group.is_own_goal
                                    ? <>
                                        <span style={{ color: '#d32f2f' }}>Bramka samobójcza</span>
                                        <SportsScoreIcon style={{ fontSize: '1.1em', color: '#d32f2f' }} />
                                      </>
                                    : <>
                                        {group.first_name} {group.last_name}
                                        {Array.from({ length: group.count }, (_, i) => (
                                            <SportsScoreIcon key={i} style={{ fontSize: '1.1em' }} />
                                        ))}
                                      </>}
                            </span>
                        </div>
                    ))
                }
            </div>
        </div>
    );

    // --- RENDER CARD LIST ---
    const renderCardList = (teamName, cards, teamId, side) => (
        <div style={{ textAlign: side === 'home' ? 'right' : 'left' }}>
            <div style={{ marginTop: '6px' }}>
                {(cards ?? []).length === 0 && (
                    <div style={{ fontSize: '0.85em', color: '#999' }}>Brak kartek</div>
                )}
                {(cards ?? []).map((card, index) => (
                    <div key={`card-${teamId}-${index}`} style={{ marginBottom: '4px', fontSize: '0.85em', display: 'flex', alignItems: 'center', justifyContent: side === 'home' ? 'flex-end' : 'flex-start', gap: '4px', whiteSpace: 'nowrap' }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                            {card.first_name} {card.last_name}
                            <CardIcon kind={card.kind} />
                        </span>
                        {isAuthenticated && (
                            <>
                                <EditNoteIcon
                                    style={{ cursor: 'pointer', fontSize: '1.1em' }}
                                    onClick={() => openEditCardModal(card, teamId)}
                                />
                                <DeleteForeverIcon
                                    style={{ cursor: 'pointer', color: 'red', fontSize: '1.1em' }}
                                    onClick={() => openDeleteCardModal(card)}
                                />
                            </>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );

    return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            {/* Match header — mirrors ResultsView card header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '20px', padding: '20px 0' }}>
                {/* Home team */}
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <Link to={`/zespol/${matchDetails.home_team_id}`} style={{ textDecoration: 'none', color: 'inherit', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        {matchDetails.home_team_logo && (
                            <img
                                src={`/logos/${matchDetails.home_team_logo}`}
                                alt={matchDetails.home_team}
                                style={{ height: '60px', marginBottom: '8px', objectFit: 'contain' }}
                                onError={(e) => (e.currentTarget.style.display = 'none')}
                            />
                        )}
                        <strong style={teamNameStyle('home')}>{matchDetails.home_team}</strong>
                    </Link>
                </div>

                {/* Score & date */}
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <span style={{ fontSize: '0.8em', color: '#666', marginBottom: '4px' }}>
                        Kolejka {matchDetails.gameweek}
                    </span>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <span style={{ fontSize: '2.5em', fontWeight: 'bold' }}>
                            {isDoubleWalkover
                                ? <span style={{ fontSize: '0.6em' }}>0:3 / 0:3</span>
                                : matchDetails.home_result != null && matchDetails.away_result != null
                                    ? `${matchDetails.home_result} - ${matchDetails.away_result}`
                                    : '– : –'}
                        </span>
                        {isAuthenticated && (
                            <EditNoteIcon
                                style={{ cursor: 'pointer', marginLeft: 8, fontSize: '1.2em' }}
                                onClick={openScoreModal}
                            />
                        )}
                    </div>
                    {walkoverLabel && (
                        <div style={{ fontSize: '0.8em', color: '#666', marginTop: '2px', textAlign: 'center' }}>
                            {walkoverLabel}
                        </div>
                    )}
                    {knockoutAnnotationLines.length > 0 && (
                        <div style={{ fontSize: '0.8em', color: '#666', marginTop: '2px', textAlign: 'center', lineHeight: 1.3 }}>
                            {knockoutAnnotationLines.map((line, i) => <div key={i}>{line}</div>)}
                        </div>
                    )}
                    <div style={{ display: 'flex', alignItems: 'center', marginTop: '4px' }}>
                        <span style={{ fontSize: '0.8em', color: '#666' }}>
                            {matchDetails.date ? formatDateTime(matchDetails.date) : 'Brak daty'}
                        </span>
                        {isAuthenticated && (
                            <EditNoteIcon
                                style={{ cursor: 'pointer', marginLeft: 6, fontSize: '1em', color: '#666' }}
                                onClick={openDateModal}
                            />
                        )}
                    </div>
                </div>

                {/* Away team */}
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <Link to={`/zespol/${matchDetails.away_team_id}`} style={{ textDecoration: 'none', color: 'inherit', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        {matchDetails.away_team_logo && (
                            <img
                                src={`/logos/${matchDetails.away_team_logo}`}
                                alt={matchDetails.away_team}
                                style={{ height: '60px', marginBottom: '8px', objectFit: 'contain' }}
                                onError={(e) => (e.currentTarget.style.display = 'none')}
                            />
                        )}
                        <strong style={teamNameStyle('away')}>{matchDetails.away_team}</strong>
                    </Link>
                </div>
            </div>

            {/* Goal count mismatch warnings (pomijane przy walkowerze) */}
            {isAuthenticated && !matchIsWalkover && matchDetails.home_result != null && (matchDetails.home_goals ?? []).length !== matchDetails.home_result && (
                <div style={{ backgroundColor: '#fff3cd', color: '#856404', border: '1px solid #ffc107', borderRadius: '4px', padding: '8px 12px', marginBottom: '10px', fontSize: '0.9em', textAlign: 'center' }}>
                    Uwaga: wynik {matchDetails.home_team} ({matchDetails.home_result}) nie zgadza się z liczbą strzelców ({(matchDetails.home_goals ?? []).length})
                </div>
            )}
            {isAuthenticated && !matchIsWalkover && matchDetails.away_result != null && (matchDetails.away_goals ?? []).length !== matchDetails.away_result && (
                <div style={{ backgroundColor: '#fff3cd', color: '#856404', border: '1px solid #ffc107', borderRadius: '4px', padding: '8px 12px', marginBottom: '10px', fontSize: '0.9em', textAlign: 'center' }}>
                    Uwaga: wynik {matchDetails.away_team} ({matchDetails.away_result}) nie zgadza się z liczbą strzelców ({(matchDetails.away_goals ?? []).length})
                </div>
            )}

            {/* Goals */}
            <h3 style={{ textAlign: 'center' }}>Bramki</h3>
            <div style={{ display: 'flex', gap: '20px' }}>
                <div style={{ flex: 1 }}>
                    {renderGoalList(matchDetails.home_team, matchDetails.home_goals, matchDetails.home_team_id, 'home')}
                </div>
                <div style={{ flex: 1 }}>
                    {renderGoalList(matchDetails.away_team, matchDetails.away_goals, matchDetails.away_team_id, 'away')}
                </div>
            </div>
            {isAuthenticated && (
                <div style={{ display: 'flex', gap: '10px', marginTop: '10px', marginBottom: '16px', justifyContent: 'center' }}>
                    <Button variant="secondary" size="sm" onClick={() => openAddGoalModal(matchDetails.home_team_id)}>
                        Dodaj bramkę dla zespołu {matchDetails.home_team}                    
                    </Button>
                    <Button variant="secondary" size="sm" onClick={() => openAddGoalModal(matchDetails.away_team_id)}>
                        Dodaj bramkę dla zespołu {matchDetails.away_team}
                    </Button>
                </div>
            )}

            {/* Cards */}
            <h3 style={{ textAlign: 'center' }}>Kartki</h3>
            <div style={{ display: 'flex', gap: '20px' }}>
                <div style={{ flex: 1 }}>
                    {renderCardList(matchDetails.home_team, matchDetails.home_cards, matchDetails.home_team_id, 'home')}
                </div>
                <div style={{ flex: 1 }}>
                    {renderCardList(matchDetails.away_team, matchDetails.away_cards, matchDetails.away_team_id, 'away')}
                </div>
            </div>
            {isAuthenticated && (
                <div style={{ display: 'flex', gap: '10px', marginTop: '10px', marginBottom: '16px', justifyContent: 'center' }}>
                    <Button variant="secondary" size="sm" onClick={() => openAddCardModal(matchDetails.home_team_id)}>
                        Dodaj kartkę dla zespołu {matchDetails.home_team}
                    </Button>
                    <Button variant="secondary" size="sm" onClick={() => openAddCardModal(matchDetails.away_team_id)}>
                        Dodaj kartkę dla zespołu {matchDetails.away_team}
                    </Button>
                </div>
            )}

            {/* Goal Add/Edit Modal */}
            <Modal show={showGoalModal} onHide={() => setShowGoalModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>{editingGoalId ? 'Edytuj bramkę' : 'Dodaj bramkę'}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {goalError && <div className="text-danger mb-2">{goalError}</div>}
                    <Form.Group className="mb-3">
                        <Form.Check
                            type="checkbox"
                            label="Bramka samobójcza"
                            checked={goalForm.is_own_goal}
                            onChange={e => setGoalForm(f => ({
                                ...f,
                                is_own_goal: e.target.checked,
                                player_id: e.target.checked ? '' : f.player_id
                            }))}
                        />
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label>Zawodnik</Form.Label>
                        <Form.Select
                            value={goalForm.player_id}
                            disabled={goalForm.is_own_goal}
                            onChange={e => setGoalForm(f => ({ ...f, player_id: e.target.value }))}
                        >
                            <option value="">Wybierz zawodnika</option>
                            {getPlayersForTeam(goalForm.team_id).map(player => (
                                <option key={player.id} value={player.id}>
                                    {player.first_name} {player.last_name}
                                </option>
                            ))}
                        </Form.Select>
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowGoalModal(false)}>Anuluj</Button>
                    <Button variant="primary" onClick={handleGoalSubmit}>Zapisz</Button>
                </Modal.Footer>
            </Modal>

            {/* Goal Delete Confirmation Modal */}
            <Modal show={showDeleteGoalModal} onHide={() => setShowDeleteGoalModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Usuń bramkę</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {deleteGoalError && <div className="text-danger mb-2">{deleteGoalError}</div>}
                    {deleteGoalTarget && (
                        <p>
                            Czy na pewno chcesz usunąć bramkę{' '}
                            {deleteGoalTarget.is_own_goal
                                ? '(samobójcza)'
                                : `${deleteGoalTarget.first_name} ${deleteGoalTarget.last_name}`}
                            ?
                        </p>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowDeleteGoalModal(false)}>Anuluj</Button>
                    <Button variant="danger" onClick={handleDeleteGoal}>Usuń</Button>
                </Modal.Footer>
            </Modal>

            {/* Card Add/Edit Modal */}
            <Modal show={showCardModal} onHide={() => setShowCardModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>{editingCardId ? 'Edytuj kartkę' : 'Dodaj kartkę'}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {cardError && <div className="text-danger mb-2">{cardError}</div>}
                    <Form.Group className="mb-3">
                        <Form.Label>Zawodnik</Form.Label>
                        <Form.Select
                            value={cardForm.player_id}
                            onChange={e => setCardForm(f => ({ ...f, player_id: e.target.value ? parseInt(e.target.value) : null }))}
                        >
                            <option value="">Wybierz zawodnika</option>
                            {getPlayersForTeam(cardForm.team_id).map(player => (
                                <option key={player.id} value={player.id}>
                                    {player.first_name} {player.last_name}
                                </option>
                            ))}
                        </Form.Select>
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label>Rodzaj kartki</Form.Label>
                        <Form.Select
                            value={cardForm.kind}
                            onChange={e => setCardForm(f => ({ ...f, kind: e.target.value }))}
                        >
                            <option value="">Wybierz rodzaj</option>
                            {CARD_OPTIONS.map(opt => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                        </Form.Select>
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowCardModal(false)}>Anuluj</Button>
                    <Button variant="primary" onClick={handleCardSubmit}>Zapisz</Button>
                </Modal.Footer>
            </Modal>

            {/* Score Modal */}
            <Modal show={showScoreModal} onHide={() => setShowScoreModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Edytuj wynik</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {scoreError && <div className="text-danger mb-2">{scoreError}</div>}
                    <Form.Group className="mb-3">
                        <Form.Label>Bramki gospodarzy ({matchDetails.home_team})</Form.Label>
                        <Form.Control
                            type="number"
                            min="0"
                            value={scoreForm.home_goals}
                            placeholder="Puste = brak wyniku"
                            disabled={scoreForm.walkover === 'both'}
                            onChange={e => setScoreForm(f => ({ ...f, home_goals: e.target.value }))}
                        />
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label>Bramki gości ({matchDetails.away_team})</Form.Label>
                        <Form.Control
                            type="number"
                            min="0"
                            value={scoreForm.away_goals}
                            placeholder="Puste = brak wyniku"
                            disabled={scoreForm.walkover === 'both'}
                            onChange={e => setScoreForm(f => ({ ...f, away_goals: e.target.value }))}
                        />
                    </Form.Group>

                    {matchDetails.type === 'cup_knockout' && !scoreForm.walkover_enabled && (
                        <>
                            <Form.Group className="mb-3">
                                <Form.Check
                                    type="checkbox"
                                    label="Rozstrzygnięte po dogrywce"
                                    checked={scoreForm.after_extra_time}
                                    onChange={e => setScoreForm(f => ({ ...f, after_extra_time: e.target.checked }))}
                                />
                            </Form.Group>

                            {scoreForm.home_goals !== '' && scoreForm.away_goals !== '' &&
                             Number(scoreForm.home_goals) === Number(scoreForm.away_goals) && (
                                <div style={{ borderTop: '1px solid #ddd', paddingTop: '12px' }}>
                                    <Form.Label>Rzuty karne (przy remisie)</Form.Label>
                                    <div style={{ display: 'flex', gap: '10px' }}>
                                        <Form.Group style={{ flex: 1 }}>
                                            <Form.Label style={{ fontSize: '0.85em' }}>Gospodarze ({matchDetails.home_team})</Form.Label>
                                            <Form.Control
                                                type="number"
                                                min="0"
                                                value={scoreForm.home_penalties}
                                                placeholder="Puste = brak"
                                                onChange={e => setScoreForm(f => ({ ...f, home_penalties: e.target.value }))}
                                            />
                                        </Form.Group>
                                        <Form.Group style={{ flex: 1 }}>
                                            <Form.Label style={{ fontSize: '0.85em' }}>Goście ({matchDetails.away_team})</Form.Label>
                                            <Form.Control
                                                type="number"
                                                min="0"
                                                value={scoreForm.away_penalties}
                                                placeholder="Puste = brak"
                                                onChange={e => setScoreForm(f => ({ ...f, away_penalties: e.target.value }))}
                                            />
                                        </Form.Group>
                                    </div>
                                </div>
                            )}
                        </>
                    )}

                    <Form.Group className="mt-3" style={{ borderTop: '1px solid #ddd', paddingTop: '12px' }}>
                        <Form.Check
                            type="checkbox"
                            label="Walkower"
                            checked={scoreForm.walkover_enabled}
                            onChange={e => handleWalkoverToggle(e.target.checked)}
                        />
                        {scoreForm.walkover_enabled && (
                            <Form.Select
                                className="mt-2"
                                value={scoreForm.walkover}
                                onChange={e => handleWalkoverChange(e.target.value)}
                            >
                                <option value="">Wybierz rodzaj walkoweru…</option>
                                <option value="home">Walkower dla gospodarzy ({matchDetails.home_team})</option>
                                <option value="away">Walkower dla gości ({matchDetails.away_team})</option>
                                {matchDetails.type !== 'cup_knockout' && (
                                    <option value="both">Walkower obustronny</option>
                                )}
                            </Form.Select>
                        )}
                        {scoreForm.walkover === 'both' && (
                            <Form.Text className="text-muted">
                                Obie drużyny przegrywają 0:3 (0 pkt).
                            </Form.Text>
                        )}
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowScoreModal(false)}>Anuluj</Button>
                    <Button variant="primary" onClick={handleSaveScore}>Zapisz</Button>
                </Modal.Footer>
            </Modal>

            {/* Date Modal */}
            <Modal show={showDateModal} onHide={() => setShowDateModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Edytuj datę i godzinę</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {dateError && <div className="text-danger mb-2">{dateError}</div>}
                    <Form.Group className="mb-3">
                        <Form.Label>Data i godzina</Form.Label>
                        <Form.Control
                            type="datetime-local"
                            value={dateForm ? dateForm.slice(0, 16) : ''}
                            onChange={e => setDateForm(e.target.value)}
                        />
                        <Form.Text className="text-muted">
                            Zostaw puste, aby usunąć datę.
                        </Form.Text>
                    </Form.Group>
                    {dateForm && (
                        <Button variant="outline-secondary" size="sm" onClick={() => setDateForm('')}>
                            Wyczyść datę
                        </Button>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowDateModal(false)}>Anuluj</Button>
                    <Button variant="primary" onClick={handleSaveDate}>Zapisz</Button>
                </Modal.Footer>
            </Modal>

            {/* Card Delete Confirmation Modal */}
            <Modal show={showDeleteCardModal} onHide={() => setShowDeleteCardModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Usuń kartkę</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {deleteCardError && <div className="text-danger mb-2">{deleteCardError}</div>}
                    {deleteCardTarget && (
                        <p>
                            Czy na pewno chcesz usunąć kartkę zawodnika{' '}
                            {deleteCardTarget.first_name} {deleteCardTarget.last_name}?
                        </p>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowDeleteCardModal(false)}>Anuluj</Button>
                    <Button variant="danger" onClick={handleDeleteCard}>Usuń</Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default MatchDetails;